// planPosesji — plan posesji: sekcje budynku pod jednym adresem (MONIT 2026-10-02_1651 + 2026-10-03_0903).
// Czyste funkcje (node --test): kody sekcji, etykiety kafelków, opis słowny, układ planu na telefonie.
//
// POJĘCIA: SEKCJA = (Hof, część, wejście). Część: VH (Vorderhaus), HH (Hinterhaus, w głębi),
// HH-L (Hinterhaus Links), HH-R (Hinterhaus Rechts); lewo i prawo patrząc od ulicy.
// Hof 1 ma VH (zawsze) + opcjonalnie HH, HH-L, HH-R; Hof 2 i dalsze nie mają VH.
// Sekcja 0 = Hof 1 · Vorderhaus · Eingang 1 = „adres jak dziś" (bez kodu, bez pasa „Jesteś tutaj").
//
// KOD (nazwy plików i folderów; DN2 = a, DN3 = c z 03.10.2026): pierwsze wejście każdej części
// NIGDY nie ma numeru; Vorderhaus wejście 1 = brak kodu; Hof ≥ 2 = prefiks HOFn_.
//   VH: '' , VH-2, VH-3   HH: HH, HH-2   HH-L: HH-L, HH-L2   HH-R: HH-R, HH-R2   Hof 2: HOF2_HH, HOF2_HH-L2
// Zero zamrażania, zero przeliczania, zero przemianowań: dodanie wejścia nie zmienia kodu istniejącej sekcji.
// NA EKRANIE (kafelki, pas) przy kilku wejściach dalej „HH-1", „HH-2", „Eingang 1".
// Nazwy części zostają niemieckie w obu językach (D14).

export const PARTS = Object.freeze(['VH', 'HH', 'HH-L', 'HH-R']);

/** Pełna nazwa części (opis słowny, licznik wejść). */
export const PART_NAME = Object.freeze({
  VH: 'Vorderhaus', HH: 'Hinterhaus', 'HH-L': 'Hinterhaus Links', 'HH-R': 'Hinterhaus Rechts',
});

/** Etykieta kafelka części z JEDNYM wejściem (D2 / makieta). */
export const PART_TILE = Object.freeze({
  VH: 'Vorderhaus', HH: 'Hinterhaus', 'HH-L': 'HH-Links', 'HH-R': 'HH-Rechts',
});

/** Etykieta części w liczniku wejść (makieta 2: „Vorderhaus", „Hinterhaus", „HH-Links", „HH-Rechts"). */
export const PART_COUNTER = PART_TILE;

/** Prefiks etykiety kafelka przy kilku wejściach: VH-1, HH-2, HH-L3, HH-R1. */
const PART_SHORT = Object.freeze({ VH: 'VH-', HH: 'HH-', 'HH-L': 'HH-L', 'HH-R': 'HH-R' });

/** Kod sekcji do nazw plików i folderów (DN2 = a, DN3 = c). Sekcja 0 = ''. */
export function sectionCode({ hof = 1, part, entrance = 1 }) {
  if (!PARTS.includes(part)) throw new TypeError(`nieznana część: ${part}`);
  const e = Number(entrance) || 1;
  let base;
  if (part === 'VH') base = e === 1 ? '' : `VH-${e}`;
  else if (part === 'HH') base = e === 1 ? 'HH' : `HH-${e}`;
  else base = e === 1 ? part : `${part}${e}`; // HH-L, HH-L2 / HH-R, HH-R2
  const h = Number(hof) || 1;
  if (h > 1) {
    if (part === 'VH') throw new TypeError('Hof ≥ 2 nie ma Vorderhaus');
    return `HOF${h}_${base}`;
  }
  return base;
}

/** Sekcja 0 = Hof 1 · Vorderhaus · Eingang 1. */
export function isSectionZero({ hof = 1, part, entrance = 1 }) {
  return Number(hof || 1) === 1 && part === 'VH' && Number(entrance || 1) === 1;
}

/** Etykieta kafelka na planie (D2): jedno wejście → nazwa części, kilka → VH-1, HH-2, HH-L3. */
export function tileLabel({ part, entrance = 1 }, entranceCount = 1) {
  if (Number(entranceCount) <= 1) return PART_TILE[part];
  return `${PART_SHORT[part]}${Number(entrance) || 1}`;
}

/**
 * Opis słowny dla technika (pas „Jesteś tutaj", pytania): „Hinterhaus Links · Eingang 2",
 * przy kilku Hofach „Hof 2 · Hinterhaus Links · Eingang 2" (wtedy zawsze, także Hof 1).
 * „Eingang n" tylko gdy część ma więcej niż jedno wejście.
 */
export function sectionDescription({ hof = 1, part, entrance = 1 }, { hofCount = 1, entranceCount = 1 } = {}) {
  const bits = [];
  if (Number(hofCount) > 1) bits.push(`Hof ${Number(hof) || 1}`);
  bits.push(PART_NAME[part]);
  if (Number(entranceCount) > 1) bits.push(`Eingang ${Number(entrance) || 1}`);
  return bits.join(' · ');
}

/**
 * Plan: { hofs: [ { hof: 1, parts: { VH: 2, HH: 2, 'HH-L': 3, 'HH-R': 1 } }, { hof: 2, parts: { HH: 1 } } ] }
 * Liczba wejść części: 0 / brak = część nie istnieje. VH w Hof 1 istnieje zawsze (min 1).
 */
export function normalizePlan(plan) {
  const hofs = (plan?.hofs || []).map((h, i) => {
    const hof = Number(h.hof) || i + 1;
    const parts = {};
    for (const p of PARTS) {
      let n = Math.max(0, Math.floor(Number(h.parts?.[p]) || 0));
      if (p === 'VH') n = hof === 1 ? Math.max(1, n) : 0;
      parts[p] = n;
    }
    return { hof, parts };
  });
  if (!hofs.length) hofs.push({ hof: 1, parts: { VH: 1, HH: 0, 'HH-L': 0, 'HH-R': 0 } });
  return { hofs };
}

/** Lista sekcji planu w stałej kolejności (Hof, VH, HH, HH-L, HH-R, wejście). */
export function sectionsOf(plan) {
  const { hofs } = normalizePlan(plan);
  const out = [];
  for (const h of hofs) {
    for (const part of PARTS) {
      const n = h.parts[part];
      for (let e = 1; e <= n; e++) {
        out.push({ hof: h.hof, part, entrance: e, entranceCount: n, code: sectionCode({ hof: h.hof, part, entrance: e }) });
      }
    }
  }
  return out;
}

/** Liczba sekcji planu (> 1 = plan jest głównym widokiem adresu, D3). */
export function sectionCount(plan) {
  return sectionsOf(plan).length;
}

/**
 * Układ jednego Hofu na telefonie (D3): HH u góry na całą szerokość (kilka wejść obok siebie,
 * numeracja od lewej), HH-L i HH-R pod nim po bokach (kilka wejść = stos, numer 1 NA DOLE,
 * najbliżej ulicy), VH na dole na całą szerokość (kilka wejść obok siebie, od lewej).
 * @returns {{ top: number[], left: number[], right: number[], bottom: number[] }} numery wejść w kolejności rysowania
 */
export function hofLayout(hofEntry) {
  const p = hofEntry?.parts || {};
  const seq = (n) => Array.from({ length: Math.max(0, Number(n) || 0) }, (_, i) => i + 1);
  return {
    top: seq(p.HH),
    left: seq(p['HH-L']).reverse(),
    right: seq(p['HH-R']).reverse(),
    bottom: seq(p.VH),
  };
}

/** Zmiana liczby wejść w edytorze: VH w Hof 1 min 1, pozostałe min 0 (0 = część nie istnieje), max 9. */
export function clampEntrances(part, hof, n) {
  const min = part === 'VH' && Number(hof) === 1 ? 1 : 0;
  return Math.min(9, Math.max(min, Math.floor(Number(n) || 0)));
}

// ─── B2 (MONIT 2026-10-03_1740): identyfikator adresu z sekcją i plan z wierszy bazy ─────────
// Ta sama konwencja co backend services/addressRef.js (B1): `recXXX` = sekcja 0 (adres jak dziś),
// `recXXX~n` = sekcja n. Trasy frontu niosą sekcję w `:id`; ekrany adresowe (Info, Notiz, nagłówek
// z nazwą i KW, lista adresów) używają czystego rec-ID.

const REF_RE = /^(rec[A-Za-z0-9]+)(?:~(\d{1,2}))?$/;

/** Czysty rec-ID adresu (bez `~n`). Nieznany format = wartość bez zmian. */
export function baseAddressId(ref) {
  const m = REF_RE.exec(String(ref ?? ''));
  return m ? m[1] : ref;
}

/** Numer sekcji z identyfikatora (`recXXX` = 0). */
export function sectionNoOf(ref) {
  const m = REF_RE.exec(String(ref ?? ''));
  return m && m[2] !== undefined ? Number(m[2]) : 0;
}

/** Czy identyfikator niesie jawną sekcję (`~n`, także `~0`)? */
export function hasSectionSuffix(ref) {
  const m = REF_RE.exec(String(ref ?? ''));
  return !!(m && m[2] !== undefined);
}

/** Identyfikator sekcji do tras: sekcja 0 w adresie z planem = `recXXX~0` (piętra VH-1), bez planu = `recXXX`. */
export function sectionRef(addressId, sectionNo, { explicitZero = false } = {}) {
  const base = baseAddressId(addressId);
  const n = Number(sectionNo) || 0;
  if (n > 0 || explicitZero) return `${base}~${n}`;
  return base;
}

/**
 * Jedna pisownia w kolejce telefonu (zasada twarda monitu: zmiana tylko w kluczu lokalizacji):
 * `recXXX~0` = `recXXX` (sekcja 0), `recXXX~n` zostaje. Stare wpisy kolejki (bez sufiksu) = sekcja 0.
 */
export function queueAddressId(ref) {
  const m = REF_RE.exec(String(ref ?? ''));
  if (!m) return ref;
  return m[2] !== undefined && Number(m[2]) > 0 ? `${m[1]}~${Number(m[2])}` : m[1];
}

/** Klucz pozycji w planie (Hof, część, wejście). */
export function positionKey({ hof = 1, part, entrance = 1 }) {
  return `${Number(hof) || 1}|${part}|${Number(entrance) || 1}`;
}

/**
 * Plan z wierszy address_sections (aktywne, bez entfallen_at): { hofs: [ { hof, parts } ] }.
 * Liczba wejść części = liczba aktywnych wierszy (wejścia 1..n, usuwane od końca).
 * Hofy bez żadnej części (np. po odznaczeniu) nie trafiają do planu.
 */
export function planFromRows(rows) {
  const byHof = new Map();
  for (const r of rows || []) {
    if (r.entfallen_at) continue;
    const hof = Number(r.hof_no ?? r.hof) || 1;
    const part = r.part;
    if (!PARTS.includes(part)) continue;
    if (!byHof.has(hof)) byHof.set(hof, { VH: 0, HH: 0, 'HH-L': 0, 'HH-R': 0 });
    const parts = byHof.get(hof);
    parts[part] = Math.max(parts[part], Number(r.entrance_no ?? r.entrance) || 1);
  }
  if (!byHof.has(1)) byHof.set(1, { VH: 1, HH: 0, 'HH-L': 0, 'HH-R': 0 });
  const hofs = [...byHof.entries()].sort((a, b) => a[0] - b[0]).map(([hof, parts]) => ({ hof, parts }));
  return normalizePlan({ hofs });
}

/**
 * Opis słowny sekcji wg planu (pas „Jesteś tutaj", pytanie o APL): Hof przy kilku Hofach,
 * „Eingang n" przy kilku wejściach części.
 */
export function describeInPlan(section, plan) {
  const p = normalizePlan(plan);
  const hof = Number(section?.hof ?? section?.hof_no) || 1;
  const entrance = Number(section?.entrance ?? section?.entrance_no) || 1;
  const hofEntry = p.hofs.find((h) => h.hof === hof);
  const entranceCount = hofEntry ? Number(hofEntry.parts[section.part]) || 1 : 1;
  return sectionDescription({ hof, part: section.part, entrance }, { hofCount: p.hofs.length, entranceCount });
}

/** Etykieta kafelka sekcji wg planu (VH-1 przy kilku wejściach, inaczej nazwa części). */
export function tileLabelInPlan(section, plan) {
  const p = normalizePlan(plan);
  const hof = Number(section?.hof ?? section?.hof_no) || 1;
  const hofEntry = p.hofs.find((h) => h.hof === hof);
  const n = hofEntry ? Number(hofEntry.parts[section.part]) || 1 : 1;
  return tileLabel({ part: section.part, entrance: Number(section?.entrance ?? section?.entrance_no) || 1 }, n);
}

/** Kod zastępczy slotu Połączenie w sekcji bez kodu (sekcja 0 po przeniesieniu APL, DN8 = a). */
export const CONN_CODE_SECTION_ZERO = 'VH';

/** Kod w nazwie pliku i folderze slotu Połączenie: kod sekcji, a dla sekcji 0 „VH" (DN8 = a). */
export function connCode(sectionCodeValue) {
  return sectionCodeValue ? String(sectionCodeValue) : CONN_CODE_SECTION_ZERO;
}

/**
 * Pakiet v81 (MONIT 2026-10-04_0101 zad. 4): nazwa ulicy pod planem („ulica · Behringstr."). Pole `strasse`
 * z Airtable, gdy jest w kontekście adresu; inaczej pierwszy człon displayName („Behringstr. 50, 12437 Berlin")
 * bez numeru domu (50, 25f, 3-5, 12 a). Brak nazwy = '' (linia bez wiszącej kropki).
 */
export function streetName({ strasse = '', displayName = '' } = {}) {
  const direct = String(strasse || '').trim();
  if (direct) return direct;
  const first = String(displayName || '').split(',')[0].trim();
  return first.replace(/\s+\d+\s*[a-zA-Z]?(\s*[-/]\s*\d+\s*[a-zA-Z]?)?$/, '').trim();
}
